import {
  axios,
  checkForValidationError,
  delay,
  ServerError,
  TServerValidationError,
} from '@/api/common'
import {
  MachineTrackingModel,
  MachineTrackingResult,
} from '@/api/hub/machineTracking/list.schema'
import { HubService } from '@/api/hub/signalr'
import { AxiosError } from 'axios'

class MachineTrackingService extends HubService {
  constructor() {
    super()
  }

  /**
   * Initiates a SignalR hub connection for machine tracking.
   * Once the connection is established, it automatically requests machine tracking data.
   * The connection is made to the '/machine-tracking' endpoint.
   *
   * Optionally, you can provide a callback to be invoked after the connection is established.
   */
  startHubConnection(onConnected?: () => void) {
    this.hub.startConnection('/machine-tracking', () => {
      this.requestMachineTrackingData()
      if (onConnected) onConnected()
    })
  }

  /**
   * Sends a request to track data for a specific machine through the hub.
   * @param machineId - Optional string identifier for the machine to track. If not provided, may track all machines.
   * @example
   * // Track a specific machine
   * requestMachineTrackingData("machine123")
   *
   * // Track all machines
   * requestMachineTrackingData()
   */
  requestMachineTrackingData(machineId?: string) {
    this.hub.sendMessage('RequestMachineTrackingData', machineId)
  }

  /**
   * Registers a callback function to handle incoming machine tracking data from the hub.
   * @param callback - Function to be called when machine tracking data is received
   * @param callback.data - The machine tracking result data that will be passed to the callback
   */
  handleReceiveMachineTrackingData(
    callback: (data: MachineTrackingResult) => void
  ) {
    this.hub.onReceiveMessage('ReceiveMachineTrackingData', callback)
  }

  /**
   * Registers a callback function to handle updates for machine tracking data.
   * @param callback - Function to be called when machine tracking data is updated.
   * The callback receives the updated machine tracking data as a parameter.
   * @param callback.data - The updated machine tracking model data
   */
  handleUpdateMachineTracking(callback: (data: MachineTrackingModel) => void) {
    this.hub.onReceiveMessage('UpdateMachineTracking', callback)
  }

  async startJob(machineId: number, workOrderScheduleId: number) {
    await delay(1000)
    try {
      const result = await axios.post(
        `api/production/prd1015/${machineId}/start`,
        { workOrderScheduleId }
      )
      return result
    } catch (error) {
      checkForValidationError(error as AxiosError<TServerValidationError>)
      throw new ServerError((error as AxiosError<string>).response?.data)
    }
  }

  async completeJob(machineId: number) {
    await delay(1000)
    try {
      const result = await axios.post(
        `api/production/prd1015/${machineId}/stop`,
        {}
      )
      return result
    } catch (error) {
      checkForValidationError(error as AxiosError<TServerValidationError>)
      throw new ServerError((error as AxiosError<string>).response?.data)
    }
  }

  async offService(machineId: number, reason: string) {
    await delay(1000)
    try {
      const result = await axios.post(
        `api/production/prd1015/${machineId}/out_of_service`,
        { reason }
      )
      return result
    } catch (error) {
      checkForValidationError(error as AxiosError<TServerValidationError>)
      throw new ServerError((error as AxiosError<string>).response?.data)
    }
  }

  async returnService(machineId: number) {
    await delay(1000)
    try {
      const result = await axios.post(
        `api/production/prd1015/${machineId}/return_to_service`,
        {}
      )
      return result
    } catch (error) {
      checkForValidationError(error as AxiosError<TServerValidationError>)
      throw new ServerError((error as AxiosError<string>).response?.data)
    }
  }
}

export const machineTrackingService = new MachineTrackingService()
